/**
 * Canonical skill-name → slug derivation, shared by the github-proxy upload
 * handler (server, authoritative) and the web Contribute form (client, advisory
 * name-availability check). Both MUST derive the identical slug, or the form
 * would report a name "available" that the server then rejects as taken (#697),
 * so this lives in `contracts` — the one package both the Lambda and the browser
 * bundle already import.
 */
export const MAX_SLUG_LENGTH = 64;

/**
 * Throwing form used by the server: an empty or all-symbol name is a request
 * error the upload handler surfaces as a 400.
 */
export function slugify(name: string): string {
  if (!name || !name.trim()) {
    throw new Error("Skill name is required");
  }

  const slug = name
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_LENGTH);

  if (!slug) {
    throw new Error("Skill name must contain at least one alphanumeric character");
  }

  return slug;
}

/**
 * Non-throwing form for UI use: returns null when `name` has no slug (empty or
 * all-symbols) so a form validator can branch instead of catching. Otherwise
 * identical to `slugify`.
 */
export function slugifyOrNull(name: string): string | null {
  try {
    return slugify(name);
  } catch {
    return null;
  }
}
